import { useMemo, useEffect, useState } from "react";
import { getFormattedDate } from "../../../../helpers/date-time-helper";
import { Card, CardBody, CardHeader, CardText, CardTitle, Col, Container, Row, Nav, NavItem, NavLink, TabContent, TabPane, Button } from "reactstrap";
import gearIconSvg from "../../../../assets/svg/gear-icon.svg";
import listIconSvg from "../../../../assets/svg/list-icon.svg";
import playIconSvg from "../../../../assets/svg/play-button.svg";
import fusionIconSvg from "../../../../assets/svg/fusion-icon.svg";
import cartIconSvg from "../../../../assets/svg/shopping-cart-icon.svg";
import { NO_DATE } from "../../../../common/constants";
import Unit from "../../../../constants/unit";
import { Spinner, ScreenOverlay, UserLink, Separator, Cell } from "../../../../components/Common";
import OperatorItem from "../operator-item";
import Map from "../map/index";
import InfoField from "../../../../components/Common/InfoField";
import EditDeviceModal from "../edit-device-modal";

import useAuthUser from "../../../../common/hooks/authUser";
import { Link, useNavigate, useParams } from "react-router-dom";
import { decodeMachineSerialFromRoute } from "../../../../constants/machineRoutes";
import { useDevice } from "../../../../common/hooks/api/device";
import { toastError } from "../../../../helpers/toast-helper";
import { getResponseItemData } from "../../../../helpers/response-helper";
import WearPartsGrid from "../../../../components/Common/WearParts/wearPartsGrid";
import {
    SEARCH_URL
} from './config';

import './styles.scss';

const DeviceDetails = () => {
    const navigate = useNavigate();
    const { user, isAdmin, isDealer } = useAuthUser();
    const { id: idParam } = useParams();
    const id = decodeMachineSerialFromRoute(idParam);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const { data: deviceResponse, isLoading } = useDevice(id, {
        onError: () => {
            toastError("Cannot get machine details");
        },
    });

    const { data } = getResponseItemData(deviceResponse);

    
    // Infer access: user belongs to owners/dealers/operators
    const hasAccess = useMemo(() => {
        if (isAdmin || isDealer) return true;
        if (!user?.id || !data) return false;

        const inOwners = (data.owners ?? []).some((u: any) => u?.id === user.id);
        const inDealers = (data.dealers ?? []).some((u: any) => u?.id === user.id);
        const inOperators = (data.operators ?? []).some((u: any) => u?.id === user.id);

        return inOwners || inDealers || inOperators;
    }, [isAdmin, isDealer, user?.id, data]);

    useEffect(() => {
        if (!isLoading && !hasAccess) {
            toastError("You do not have access to this machine.");
            navigate("/machines");
        }
    }, [isLoading, hasAccess, navigate]);


    const formattedRegisteredDate = useMemo(() => data?.created ? getFormattedDate(data?.created, "M/D/YYYY") : '-', [data?.created]);
    const formattedLastActiveDate = useMemo(() => data?.lastActive ? (data?.lastActive === NO_DATE ? '-' : getFormattedDate(data.lastActive, "M/D/YYYY")) : '', [data?.lastActive]);
    const totalHours = (data?.telemetryData?.toolHours || data?.telemetryData?.toolMinutes) ?  `${data?.telemetryData?.toolHours || 0} H ${Math.round(data?.telemetryData?.toolMinutes) || 0} M` : '-';

    const operators = data?.operators?.map((user) => (
        <OperatorItem key={`operator-${user.id}`} user={user} isAdmin={isAdmin} isDealer={isDealer} />
    ));

    const dealer = data?.dealers?.[0];
    const owner = data?.owners?.[0];

    const hasLastKnownLocation = useMemo(() => !!(data?.telemetryData?.latitude && data?.telemetryData?.longitude), [data?.telemetryData]);
    const searchLink = useMemo(() => (SEARCH_URL && data?.serialNumber) ? `${SEARCH_URL}?s=%5b${data?.serialNumber}%5d&exact=true` : '', [data?.serialNumber]);
    
    


    // Fusion tab is visible only if fusion === 1 or fusion == null
    const showFusionTab = (data?.fusion === 1 || data?.fusion == null);

    // Which tab is active
    const initialTab: 'machine-info' | 'fusion' = showFusionTab ? 'fusion' : 'machine-info';
    const [activeTab, setActiveTab] = useState<'machine-info' | 'fusion'>(initialTab);


    // If fusion tab becomes hidden (e.g., after load), force back to machine-info
    useEffect(() => {
    if (!showFusionTab && activeTab === 'fusion') {
        setActiveTab('machine-info');
    }
    }, [showFusionTab, activeTab]);


    return (
        <>
            <Container fluid className="device-details">
                <Row>
                    <Col sm="2"  xxl="2" className="d-flex flex-column">
                        {data?.imgUrl
                            ? (<img src={data?.imgUrl} width="100%" />)
                            : (<div className="h-100 d-flex flex-column justify-content-center align-items-center text-muted">
                                No image
                              </div>)
                        }
                    </Col>
                    <Col sm="3" xxl="3" className="d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-start">
                            <InfoField
                                title="Serial Number:"
                                value={data?.serialNumber || `?`}
                            />
                            {isAdmin && (
                                <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => setIsEditModalOpen(true)}
                                    className="ms-2"
                                >
                                    Edit Device
                                </Button>
                            )}
                        </div>
                        <InfoField
                            title="Owner:"
                        >
                            {isLoading ? '?' : (
                                owner
                                    ? <UserLink user={owner} withArrow className="text-black" isAdmin={isAdmin} isDealer={isDealer} />
                                    : "-"
                            )}
                        </InfoField>
                    </Col>
                    <Col sm="3" xxl="3" className="d-flex flex-column">
                        <InfoField
                            title="Date Registered:"
                            value={isLoading ? '?' : formattedRegisteredDate}
                        />
                        <InfoField
                            title="Dealer:"
                        >
                            {isLoading ? '?' : (dealer
                                    ? <UserLink user={dealer} withArrow className="text-black" isAdmin={isAdmin} isDealer={isDealer} />
                                    : '-'
                            )}
                        </InfoField>
                    </Col>

                    <Col sm="4" xxl="4" className="d-flex flex-column">
                        <p>
                            
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => setActiveTab('machine-info')}
                                >
                                <img src={cartIconSvg} className="me-2" alt="" aria-hidden="true" />
                                Buy Mulching Teeth
                            </button>

                        </p>
                        <p>
                            {isAdmin && data?.serialNumber ? (
                                <Link
                                    to="/admin/documoto-parts"
                                    state={{ serial: data.serialNumber, autoSearch: true }}
                                    className="btn btn-outline-secondary"
                                >
                                    <img src={gearIconSvg} className="me-2" alt="" aria-hidden="true" />
                                    Parts & Accessories
                                </Link>
                            ) : (
                                <a href={searchLink} className="btn btn-outline-secondary" target="_blank" rel="noopener noreferrer">
                                    <img src={gearIconSvg} className="me-2" alt="" aria-hidden="true" />
                                    Parts & Accessories
                                </a>
                            )}
                        </p>

                        <p>
                            <a href={searchLink} className="btn btn-outline-secondary" target="_blank" rel="noopener noreferrer">
                                <img src={listIconSvg} className="me-2" alt="" aria-hidden="true" />
                                Manuals
                            </a>
                        </p>
                        <p>
                            <a href="https://www.youtube.com/watch?v=g6ZzE7-9Ocg&list=PLuzMwWELZ1qpLld0BWJPUg-j83eFAFBHO" className="btn btn-outline-secondary" target="blank" rel='noopener noreferrer'>
                                <img
                                    src={playIconSvg}
                                    className="me-2"
                                />
                                Training Videos
                            </a>
                        </p>
                        
                    </Col>
                </Row>
            </Container>

            <Separator />

            
            {/* ---- TABS ---- */}
            <Container fluid className="mt-3">
            
            <Nav tabs className="custom-tabs">
                
                {showFusionTab && (
                <NavItem>
                    <NavLink
                    className={`custom-tab ${activeTab === 'fusion' ? 'active' : ''}`}
                    onClick={() => setActiveTab('fusion')}
                    style={{ cursor: 'pointer' }}
                    >
                    <img src={fusionIconSvg} className="tab-icon" alt="" aria-hidden="true" />
                    <span className="tab-text">  Fusion</span>
                    </NavLink>
                </NavItem>
                )}

                <NavItem>
                <NavLink
                    className={`custom-tab ${activeTab === 'machine-info' ? 'active' : ''}`}
                    onClick={() => setActiveTab('machine-info')}
                    style={{ cursor: 'pointer' }}
                >
                    
                <img src={gearIconSvg} className="tab-icon" alt="" aria-hidden="true" />
                <span className="tab-text">  Machine Info/Buy Parts</span>

                </NavLink>
                </NavItem>
            </Nav>


            <TabContent activeTab={activeTab} className="custom-tabcontent pt-3">
                

                {/* ---------- FUSION TAB (conditionally rendered) ---------- */}
                {showFusionTab && (
                <TabPane tabId="fusion">
                    {/* You mentioned “the container device-details-metrics” */}
                    {/* Including BOTH metrics containers from your snippet inside Fusion tab */}

                    {/* Metrics: pressures, RPM, flow, temp, location, hours, operators */}
                    <Container fluid className="device-details-metrics">
                    <Row>
                        <Col xs={12} md={6} xl={3}>
                        <Card>
                            <CardBody className="d-flex flex-column align-items-center justify-content-center">
                            <CardText className="total-count mb-0">
                                <Cell
                                value={parseFloat(((data?.telemetryData?.avgHydraulicPressure ?? 0) * 14.5038).toFixed(2))}
                                isFloatNumber
                                isLoading={isLoading}
                                />
                            </CardText>
                            <CardText className="total-count-descr">Avg Hydraulic Pressure (PSI)</CardText>
                            </CardBody>
                        </Card>
                        </Col>

                        <Col xs={12} md={6} xl={3}>
                        <Card>
                            <CardBody className="d-flex flex-column align-items-center justify-content-center">
                            <CardText className="total-count mb-0">
                                <Cell
                                value={data?.telemetryData?.toolSpeedActualRPM}
                                isFloatNumber
                                isLoading={isLoading}
                                />
                            </CardText>
                            <CardText className="total-count-descr">Avg Motor Speed (RPM)</CardText>
                            </CardBody>
                        </Card>
                        </Col>

                        <Col xs={12} md={6} xl={3}>
                        <Card>
                            <CardBody className="d-flex flex-column align-items-center justify-content-center">
                            <CardText className="total-count mb-0">
                                <Cell
                                value={parseFloat(((data?.telemetryData?.flowRate ?? 0) * 0.2642).toFixed(2))}
                                isFloatNumber
                                isLoading={isLoading}
                                />
                            </CardText>
                            <CardText className="total-count-descr">Avg Carrier Flow (GPM)</CardText>
                            </CardBody>
                        </Card>
                        </Col>

                        <Col xs={12} md={6} xl={3}>
                        <Card>
                            <CardBody className="d-flex flex-column align-items-center justify-content-center">
                            <CardText className="total-count mb-0">
                                <Cell
                                value={parseFloat((((data?.telemetryData?.oilTemperatureC ?? 0) * 9) / 5 + 32).toFixed(2))}
                                isFloatNumber
                                isLoading={isLoading}
                                />
                            </CardText>
                            <CardText className="total-count-descr">Avg Oil Temperature (F)</CardText>
                            </CardBody>
                        </Card>
                        </Col>

                        <Col md={6} className="pb-4">
                        <Card className="h-100">
                            <CardHeader>
                            <CardTitle>Last Known Location</CardTitle>
                            </CardHeader>
                            <CardBody>
                            {hasLastKnownLocation ? (
                                <div style={{ height: '400px' }}>
                                <Map
                                    defaultCenter={{
                                    lat: data!.telemetryData.latitude!,
                                    lng: data!.telemetryData.longitude!,
                                    }}
                                />
                                </div>
                            ) : (
                                <div className="total-count mb-0">{isLoading ? '?' : '-'}</div>
                            )}
                            </CardBody>
                        </Card>
                        </Col>

                        <Col md={6} className="d-flex flex-column">
                        <Card>
                            <CardBody className="d-flex flex-column align-items-center justify-content-center">
                            <CardText className="total-count mb-0">{totalHours}</CardText>
                            <CardText className="total-count-descr">Machine Hours</CardText>
                            </CardBody>
                        </Card>

                        <Card className="h-100">
                            <CardHeader>
                            <CardTitle>Machine Operators</CardTitle>
                            </CardHeader>
                            <CardBody>
                            <Container fluid>
                                {!isLoading && (
                                operators?.length ? (
                                    operators
                                ) : (
                                    <Row>
                                    <Col className="total-count-descr">-</Col>
                                    </Row>
                                )
                                )}
                            </Container>
                            </CardBody>
                        </Card>
                        </Col>
                    </Row>
                    </Container>

                    {/* Diagnostics metrics container */}
                    <Container fluid className="device-details-metrics">
                    <Row>
                        <Col md={3}>
                        <Card className="h-100">
                            <CardBody className="d-flex flex-column diagnostics-box">
                            <CardText className="total-count mb-0">
                                <Cell value={data?.motorSizeCC} unit={Unit.CC} isLoading={isLoading} />
                            </CardText>
                            <CardText className="total-count-descr">Motor Displacement</CardText>
                            </CardBody>
                        </Card>
                        </Col>

                        <Col md={3}>
                        <Card className="h-100">
                            <CardBody className="d-flex flex-column diagnostics-box h-100">
                            <CardText className="total-count mb-0">
                                <Cell value={data?.motorSprocketTeeth} unit={Unit.TEETH} isLoading={isLoading} />
                            </CardText>
                            <CardText className="total-count-descr">Motor Sprocket Size</CardText>
                            </CardBody>
                        </Card>
                        </Col>

                        <Col md={3}>
                        <Card className="h-100">
                            <CardBody className="d-flex flex-column diagnostics-box h-100">
                            <CardText className="total-count mb-0">
                                <Cell value={data?.motorRotorSprocketTeeth} unit={Unit.TEETH} isLoading={isLoading} />
                            </CardText>
                            <CardText className="total-count-descr">Rotor Sprocket Size</CardText>
                            </CardBody>
                        </Card>
                        </Col>

                        <Col md={3}>
                        <Card className="h-100">
                            <CardBody className="d-flex flex-column diagnostics-box h-100">
                            <CardText className="total-count mb-0">
                                <Cell
                                value={data?.telemetryData?.maxPressureBar}
                                unit={Unit.PSI}
                                isFloatNumber
                                isLoading={isLoading}
                                />
                            </CardText>
                            <CardText className="total-count-descr">Max System PSI</CardText>
                            </CardBody>
                        </Card>
                        </Col>
                    </Row>
                    </Container>
                </TabPane>
                )}
                {/* ---------- MACHINE INFO TAB ---------- */}
                <TabPane tabId="machine-info">
                <Container fluid className="mt-4">
                    <WearPartsGrid
                        productCodeType={data?.productCodeType}
                        serialNumber={data?.serialNumber}
                    />
                </Container>
                <Separator />
                <Container fluid>
                    <h3 className="wear-grid__title">Machine Information</h3>
                    <p className="wear-grid__subtitle text-muted">
                        Machine Specs, Manauals, Videos, and Spare Parts lookup
                    </p>
                    <Row>
                    <Col md={6} xl={4} className="d-flex flex-column">
                        {/* The 3 buttons moved here */}
                        <p>
                            {isAdmin && data?.serialNumber ? (
                                <Link
                                    to="/admin/documoto-parts"
                                    state={{ serial: data.serialNumber, autoSearch: true }}
                                    className="btn btn-outline-secondary"
                                >
                                    <img src={gearIconSvg} className="me-2" alt="" aria-hidden="true" />
                                    Parts & Accessories
                                </Link>
                            ) : (
                                <a href={searchLink} className="btn btn-outline-secondary" target="_blank" rel="noopener noreferrer">
                                    <img src={gearIconSvg} className="me-2" alt="" aria-hidden="true" />
                                    Parts & Accessories
                                </a>
                            )}
                        </p>

                        <p>
                            <a href={searchLink} className="btn btn-outline-secondary" target="_blank" rel="noopener noreferrer">
                                <img src={listIconSvg} className="me-2" alt="" aria-hidden="true" />
                                Manuals
                            </a>
                        </p>
                        <p>
                            <a href="https://www.youtube.com/watch?v=g6ZzE7-9Ocg&list=PLuzMwWELZ1qpLld0BWJPUg-j83eFAFBHO" className="btn btn-outline-secondary" target="blank" rel='noopener noreferrer'>
                                <img
                                    src={playIconSvg}
                                    className="me-2"
                                />
                                Training Videos
                            </a>
                        </p>
                    </Col>

                    {/* Item & ProductCodeType InfoFields */}
                    <Col md={6} xl={4} className="d-flex flex-column">
                        <InfoField
                            title="Serial Number:"
                            value={data?.serialNumber || `?`}
                        />
                        <InfoField
                            title="Machine Type:"
                            value={
                                (data?.fusion === 1 || data?.fusion == null)
                                ? 'Fusion'
                                : (data?.machineName ?? '?')
                            }

                        />
                        <InfoField
                            title="Date Last Active:"
                            value={formattedLastActiveDate || '?'}
                        />
                        
                    </Col>
                    <Col md={6} xl={4} className="d-flex flex-column">
                        <InfoField title="Item:" value={data?.item || `?`} />
                        <InfoField title="Product Code Type:" value={data?.productCodeType || `?`} />
                        <InfoField
                            title="Date Registered:"
                            value={isLoading ? '?' : formattedRegisteredDate}
                        />
                    </Col>
                    </Row>
                </Container>
                
                

                </TabPane>
            </TabContent>
            </Container>

            {isLoading && (
                <>
                    <ScreenOverlay />
                    <Spinner />
                </>
            )}
            {isAdmin && (
                <EditDeviceModal
                    isOpen={isEditModalOpen}
                    onToggle={() => setIsEditModalOpen(false)}
                    device={data}
                />
            )}
        </>
    );
};

export default DeviceDetails;
